<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center min-h-[400px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <i class="fas fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
      <p class="text-red-600 text-lg">{{ error }}</p>
    </div>

    <!-- TCR Details -->
    <div v-else-if="tcr" class="space-y-8">
      <!-- Header Section -->
      <div class="bg-white rounded-xl shadow-lg p-4">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <div class="flex items-center justify-between mb-3">
              <h1 class="text-2xl font-bold text-white bg-primary-600 px-4 py-2 rounded-lg shadow-md transition-all duration-300 hover:bg-primary-700">
                {{ tcr.tcr_name }}
              </h1>
              <div class="flex items-center space-x-3">
                <span class="px-3 py-1 text-sm rounded-full" 
                  :class="tcr.mhc_class === 'I' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'">
                  {{ tcr.mhc_class }}
                </span>
                <button
                  @click="router.back()"
                  class="px-3 py-1 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center text-sm"
                >
                  <i class="fas fa-arrow-left mr-1.5"></i>
                  Back to Search
                </button>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <!-- Basic Info -->
              <div class="bg-gray-50 rounded-lg p-3">
                <h3 class="text-base font-medium text-gray-900 mb-2">Basic Info</h3>
                <div class="space-y-1.5">
                  <div class="flex justify-between">
                    <span class="text-gray-600">Complex ID:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.complex_id }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">Species:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.species }}</span>
                  </div>
                </div>
              </div>

              <!-- Alpha Chain -->
              <div class="bg-gray-50 rounded-lg p-3">
                <h3 class="text-base font-medium text-gray-900 mb-2">Alpha Chain</h3>
                <div class="space-y-1.5">
                  <div class="flex justify-between">
                    <span class="text-gray-600">CDR3α:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.cdr3a }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">TRAV:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.trav }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">TRAJ:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.traj }}</span>
                  </div>
                </div>
              </div>

              <!-- Beta Chain -->
              <div class="bg-gray-50 rounded-lg p-3">
                <h3 class="text-base font-medium text-gray-900 mb-2">Beta Chain</h3>
                <div class="space-y-1.5">
                  <div class="flex justify-between">
                    <span class="text-gray-600">CDR3β:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.cdr3b }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">TRBV:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.trbv }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-gray-600">TRBJ:</span>
                    <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.trbj }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <!-- pMHC Information -->
      <div class="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div class="flex items-center justify-between cursor-pointer bg-primary-600 rounded-lg px-3 py-1.5 hover:bg-primary-700 transition-colors duration-200" @click="toggleSection('pMHC')">
          <h2 class="text-lg font-semibold text-white flex items-center">
            <i class="fas fa-virus mr-2"></i>
            pMHC Information
          </h2>
          <i :class="['fas', expandedSections.pMHC ? 'fa-chevron-up' : 'fa-chevron-down', 'text-white text-base']"></i>
        </div>
        <div v-show="expandedSections.pMHC" class="mt-3">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- MHC Info -->
            <div class="bg-gray-50 rounded-lg p-3">
              <h3 class="text-base font-medium text-gray-900 mb-2">MHC Info</h3>
              <div class="space-y-1.5">
                <div class="flex justify-between">
                  <span class="text-gray-600">MHC A:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.mhc_a }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">MHC B:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.mhc_b }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">MHC Mutation:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.mhc_mutation || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">MHC Class:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.mhc_class }}</span>
                </div>
              </div>
            </div>

            <!-- Epitope Info -->
            <div class="bg-gray-50 rounded-lg p-3">
              <h3 class="text-base font-medium text-gray-900 mb-2">Epitope Info</h3>
              <div class="space-y-1.5">
                <div class="flex justify-between">
                  <span class="text-gray-600">Epitope:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.epitope_sequence }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Mutation:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.epitope_mutation || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Epitope Gene:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.epitope_gene }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Peptide Mutation:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.epitope_mutation}}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Affinity Information -->
      <div class="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div class="flex items-center justify-between cursor-pointer bg-primary-600 rounded-lg px-3 py-1.5 hover:bg-primary-700 transition-colors duration-200" @click="toggleSection('affinity')">
          <h2 class="text-lg font-semibold text-white flex items-center">
            <i class="fas fa-chart-line mr-2"></i>
            Affinity and Structure Information
          </h2>
          <i :class="['fas', expandedSections.affinity ? 'fa-chevron-up' : 'fa-chevron-down', 'text-white text-base']"></i>
        </div>
        <div v-show="expandedSections.affinity" class="mt-3">
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="space-y-1.5">
              <div class="flex justify-between">
                <span class="text-gray-600">2D Affinity:</span>
                <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.affinity_2d || 'N/A' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">3D Affinity:</span>
                <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.affinity_3d || 'N/A' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Catch Bond:</span>
                <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.catch_bond || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- EC50 Information -->
      <div class="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div class="flex items-center justify-between cursor-pointer bg-primary-600 rounded-lg px-3 py-1.5 hover:bg-primary-700 transition-colors duration-200" @click="toggleSection('ec50')">
          <h2 class="text-lg font-semibold text-white flex items-center">
            <i class="fas fa-flask mr-2"></i>
            EC50 Information
          </h2>
          <i :class="['fas', expandedSections.ec50 ? 'fa-chevron-up' : 'fa-chevron-down', 'text-white text-base']"></i>
        </div>
        <div v-show="expandedSections.ec50" class="mt-3">
          <div class="bg-gray-50 rounded-lg p-3">
            <div class="grid grid-cols-4 gap-3">
              <!-- Labels -->
              <div class="space-y-2">
                <div class="text-gray-600">Measurement:</div>
                <div class="text-gray-600">EC50:</div>
                <div class="text-gray-600">E score:</div>
                <div class="text-gray-600">Co-receptor:</div>
                <div class="text-gray-600">Confidence level:</div>
              </div>
              
              <!-- Cytokine -->
              <div class="space-y-2">
                <div class="text-sm font-medium text-gray-900 mb-1">Cytokine release</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.ec50_1 ? parseFloat(tcr.ec50_1).toFixed(4) : 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.e_1 ? parseFloat(tcr.e_1).toFixed(4) : 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.co_receptor_1 || 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.confidence_level_1 || 'N/A' }}</div>
              </div>

              <!-- Activation -->
              <div class="space-y-2">
                <div class="text-sm font-medium text-gray-900 mb-1">Activation marker</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.ec50_2 ? parseFloat(tcr.ec50_2).toFixed(4) : 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.e_2 ? parseFloat(tcr.e_2).toFixed(4) : 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.co_receptor_2 || 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.confidence_level_2 || 'N/A' }}</div>
              </div>

              <!-- Lysis -->
              <div class="space-y-2">
                <div class="text-sm font-medium text-gray-900 mb-1">Cell killing</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.ec50_3 ? parseFloat(tcr.ec50_3).toFixed(4) : 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.e_3 ? parseFloat(tcr.e_3).toFixed(4) : 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.co_receptor_3 || 'N/A' }}</div>
                <div class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.confidence_level_3 || 'N/A' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Other Information -->
      <div class="bg-white rounded-xl shadow-lg p-4">
        <div class="flex items-center justify-between cursor-pointer bg-primary-600 rounded-lg px-3 py-1.5 hover:bg-primary-700 transition-colors duration-200" @click="toggleSection('other')">
          <h2 class="text-lg font-semibold text-white flex items-center">
            <i class="fas fa-info-circle mr-2"></i>
            Other Information
          </h2>
          <i :class="['fas', expandedSections.other ? 'fa-chevron-up' : 'fa-chevron-down', 'text-white text-base']"></i>
        </div>
        <div v-show="expandedSections.other" class="mt-3">
          <div class="space-y-3">
            <div class="bg-gray-50 rounded-lg p-3">
              <h3 class="text-base font-medium text-gray-900 mb-2">Function & Disease</h3>
              <div class="space-y-1.5">
                <div class="flex justify-between">
                  <span class="text-gray-600">Function:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.function || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Disease:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.disease || 'N/A' }}</span>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 rounded-lg p-3">
              <h3 class="text-base font-medium text-gray-900 mb-2">Source & Reference</h3>
              <div class="space-y-1.5">
                <div class="flex justify-between">
                  <span class="text-gray-600">Donor Source:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.species || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Cell Source:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.species || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Reference:</span>
                  <span class="font-mono bg-white px-2 py-0.5 rounded">{{ tcr.reference || 'N/A' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

            <!-- Structure Viewer Section -->
            <div class="bg-white rounded-xl shadow-lg p-4 mb-4">
        <div class="flex items-center justify-between cursor-pointer bg-primary-600 rounded-lg px-3 py-1.5 hover:bg-primary-700 transition-colors duration-200" @click="toggleSection('structure')">
          <h2 class="text-lg font-semibold text-white flex items-center">
            <i class="fas fa-cube mr-2"></i>
            3D Structure
          </h2>
          <i class="fas" :class="expandedSections.structure ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-if="expandedSections.structure" class="mt-4">
          <!-- <div v-if="tcr.pdb_id" class="space-y-4">
            <div class="text-sm text-gray-500 mb-2">
              PDB ID: {{ tcr.pdb_id }}
            </div>
            <PDBViewer 
              v-if="expandedSections.structure"
              :key="tcr.pdb_id" 
              :pdb-id="tcr.pdb_id" 
              class="border border-gray-200 rounded-lg"
            />
            <div class="flex justify-end space-x-4">
              <a 
                :href="`https://www.rcsb.org/structure/${tcr.pdb_id}`"
                target="_blank"
                class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <i class="fas fa-external-link-alt"></i>
                View on RCSB PDB
              </a>
              <a 
                :href="`https://www.ebi.ac.uk/pdbe/entry/pdb/${tcr.pdb_id}`"
                target="_blank"
                class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <i class="fas fa-external-link-alt"></i>
                View on PDBe
              </a>
            </div>
          </div> -->
          <div v-if="tcr.pdb_id"  class="space-y-4">
            <!-- 拆分 pdb_id（支持单个或多个 ID，用 / 分隔） -->
            <template v-for="(singlePdbId, index) in tcr.pdb_id.split('/')"  :key="index">
              <div class="text-sm text-gray-500 mb-2">
                PDB ID: {{ singlePdbId.trim()  }}  <!-- 去除可能的空格 -->
              </div>
              <PDBViewer 
                v-if="expandedSections.structure" 
                :key="singlePdbId" 
                :pdb-id="singlePdbId.trim()"  
                class="border border-gray-200 rounded-lg"
              />
              <div class="flex justify-end space-x-4">
                <a 
                  :href="`https://www.rcsb.org/structure/${singlePdbId.trim()}`" 
                  target="_blank"
                  class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <i class="fas fa-external-link-alt"></i>
                  View on RCSB PDB 
                </a>
                <a 
                  :href="`https://www.ebi.ac.uk/pdbe/entry/pdb/${singlePdbId.trim()}`" 
                  target="_blank"
                  class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <i class="fas fa-external-link-alt"></i>
                  View on PDBe 
                </a>
              </div>
            </template>
          </div>

          <div v-else class="text-center py-8 text-gray-500">
            <i class="fas fa-cube text-4xl mb-2"></i>
            <p>No structure available for this TCR complex</p>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else class="bg-white rounded-xl shadow-lg p-6 text-center">
      <i class="fas fa-database text-gray-400 text-4xl mb-4"></i>
      <p class="text-gray-600 text-lg">No TCR data available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import PDBViewer from '@/components/PDBViewer.vue'
import { tcrApi } from '@/api/tcr'

interface TCRComplex {
  complex_id: string
  tcr_name: string
  cdr3a: string
  trav: string
  traj: string
  cdr3b: string
  trbv: string
  trbj: string
  species: string
  mhc_a: string
  mhc_b: string
  mhc_mutation: string
  mhc_class: string
  epitope_gene: string
  epitope_mutation: string
  epitope_sequence: string
  affinity_2d: string | null
  affinity_3d: string | null
  catch_bond: string | null
  ec50_1: string | null
  e_1: string | null
  co_receptor_1: string | null
  confidence_level_1: string | null
  ec50_2: string | null
  e_2: string | null
  co_receptor_2: string | null
  confidence_level_2: string | null
  ec50_3: string | null
  e_3: string | null
  co_receptor_3: string | null
  confidence_level_3: string | null
  function: string | null
  disease: string | null
  pdb_id: string | null
  reference: string | null
}

const router = useRouter()
const route = useRoute()
const tcr = ref<TCRComplex | null>(null)
const loading = ref(true)
const error = ref('')

// 添加展开/收起状态控制
const expandedSections = ref({
  pMHC: true,
  affinity: true,
  ec50: true,
  other: true,
  structure: false // 默认关闭结构查看器
})

const toggleSection = (section: keyof typeof expandedSections.value) => {
  expandedSections.value[section] = !expandedSections.value[section]
}

const fetchTCRDetail = async () => {
  try {
    loading.value = true
    error.value = ''
    
    const id = route.params.id
    if (!id || typeof id !== 'string') {
      throw new Error('TCR ID is missing or invalid')
    }

    const data = await tcrApi.getTCRDetail(id)
    if (!data || !data.complex_id) {
      throw new Error('Invalid data format from server')
    }

    tcr.value = data as TCRComplex
  } catch (err) {
    console.error('Error fetching TCR detail:', err)
    error.value = err instanceof Error ? err.message : 'Failed to load TCR details. Please try again later.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchTCRDetail()
})
</script> 